import { Panel, SectionHeader, ErpTable, Th, Td, EmptyRow } from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { useState } from 'react'
import { Modal, Field } from '@/shared/ui/Modal'
import { EMPTY_UNIT_FILTERS, listUnits } from '@/features/geography/api'
import { explain } from '@/shared/api/problem'
import { archiveCell, cellStatuses, createCell, deleteCell, listCells } from './api'
import { FilterBar } from '@/shared/ui/filters'
import { CellDrawer } from './CellDrawer'
import { ElectionPicker } from './ElectionPicker'
import { useElection } from './useElection'
import { label } from './vocab'
import type { Country } from 'react-phone-number-input'
import { PhoneInput, countryOf, DEFAULT_COUNTRY } from '@/components/erp/phone-input'

/**
 * Cells — list with key metrics per cell. Drawer with member, coordinator,
 * pledge and award detail is W2/W3; this screen is the W1 list.
 */
export function CellsPage() {
  const canRead = useCan('turnout:read')
  const selection = useElection()
  const { electionId } = selection
  const [creating, setCreating] = useState(false)
  const [openCell, setOpenCell] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [rowError, setRowError] = useState<string | null>(null)

  // Delete is refused for a cell with history; the server's sentence explains and points
  // at Archive, so it is shown rather than replaced.
  const act = async (work: () => Promise<unknown>) => {
    setBusy(true); setRowError(null)
    try { await work(); cells.reload() } catch (e) { setRowError(explain(e)) }
    finally { setBusy(false) }
  }
  const [status, setStatus] = useState('')
  const [stationFilter, setStationFilter] = useState('')
  const statuses = useLoad(cellStatuses, [])
  const filterStations = useLoad(
    () => listUnits({ ...EMPTY_UNIT_FILTERS, type: ['POLLING_STATION'] }, 0, 200), [])
  const cells = useLoad(
    () => (electionId
      ? listCells({ electionId, status: status || undefined,
          stationId: stationFilter || undefined })
      : Promise.resolve([])),
    [electionId, status, stationFilter])

  if (!canRead) return <Forbidden what="Turnout cells" />

  const rows = (cells.data ?? [])

  return (
    <div className="space-y-4">
      <SectionHeader title="Cells" description="Each cell is ~10 members grouped by proximity, sharing a coordinator."
        right={(
          <span className="flex items-center gap-2">
            <ElectionPicker selection={selection} />
            {/* Cells are built by hand — the assignment engine is an accelerator, not the
                only way in. This control did not exist, so the manual path the server has
                always supported was unreachable from the UI. */}
            <button type="button" className="erp-btn erp-btn-primary h-9"
              disabled={!electionId} onClick={() => setCreating(true)}>
              New cell
            </button>
          </span>
        )}
      />
      <ErrorStrip message={rowError ?? selection.error ?? cells.error} />

      <FilterBar>
        <label className="flex items-center gap-1.5 text-erp-xs">
          Status
          <select className="erp-input h-9" value={status}
            disabled={statuses.loading}
            onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            {(statuses.data ?? []).map((c) => (
              <option key={c} value={c}>{label(c)}</option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1.5 text-erp-xs">
          Polling station
          <select className="erp-input h-9" value={stationFilter}
            disabled={filterStations.loading}
            onChange={(e) => setStationFilter(e.target.value)}>
            <option value="">All stations</option>
            {(filterStations.data?.content ?? []).map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
        </label>
      </FilterBar>
      {cells.loading && rows.length === 0 ? <LoadingBlock label="Loading cells…" /> : (
        <Panel title="All cells">
          <ErpTable head={(
            <>
              {/* Order must match the <Td> order below — these were swapped, so every
                  row showed the station under "Members" and the count under "Polling
                  station". */}
              <Th>Cell</Th>
              <Th>Status</Th>
              <Th>Polling station</Th>
              <Th className="text-right">Members</Th>
              <Th>Coordinator</Th>
              <Th>Contact</Th>
              <Th>Located</Th>
              <Th />
            </>
          )}>
            {rows.length === 0 ? (
              <EmptyRow colSpan={8} message="No cells yet. Use the Cell builder to generate a proposal." />
            ) : rows.map((c) => (
              <tr key={c.id} className="hover:bg-muted/40 cursor-pointer"
                onClick={() => setOpenCell(c.id)}>
                <Td>
                  {/* The NAME, not a uuid slice. A coordinator reads this list to find
                      their own cell; eight hex characters identify nothing to a human. */}
                  <span className="block font-medium">{c.label ?? c.code}</span>
                  <span className="block font-mono text-erp-xs text-muted-foreground">
                    {c.code}
                  </span>
                </Td>
                <Td className="text-muted-foreground">{label(c.statusCode)}</Td>
                <Td className="text-muted-foreground">
                  {c.stationName ?? c.stationId.slice(0, 8)}
                </Td>
                <Td className="text-right tabular-nums">{c.memberCount}</Td>
                <Td className="text-muted-foreground">
                  {c.coordinatorMemberId ? 'Assigned' : <span className="text-amber-600 font-medium">Needs coordinator</span>}
                </Td>
                {/* Pledge, award and flag counts were rendered here from fields the
                    server does not send — they showed blank on every row. The code is
                    a real field and is what a coordinator reads off a printed list. */}
                <Td className="text-muted-foreground">
                  {c.contactName ?? '—'}
                  {c.contactPhoneMask && (
                    <span className="block font-mono text-erp-xs">{c.contactPhoneMask}</span>
                  )}
                </Td>
                <Td className="text-muted-foreground">
                  {/* A cell with no point cannot be found on the ground, which is worth
                      seeing at a glance rather than only in the drawer. */}
                  {c.latitude != null && c.longitude != null
                    ? `${c.latitude.toFixed(4)}, ${c.longitude.toFixed(4)}`
                    : <span className="text-amber-700">not located</span>}
                </Td>
                <Td className="text-right whitespace-nowrap">
                  {/* stopPropagation on the wrapper: the row itself opens the drawer, and
                      a click on Delete must not do both. */}
                  <span onClick={(e) => e.stopPropagation()}>
                  <span className="inline-flex items-center gap-2">
                    <button type="button" className="erp-btn-ghost h-7 text-erp-xs"
                      disabled={busy}
                      onClick={() => { void act(() => archiveCell(c.id, 'SUPERSEDED')) }}>
                      Archive
                    </button>
                    <button type="button" className="erp-btn-ghost h-7 text-erp-xs text-red-700"
                      disabled={busy}
                      onClick={() => { void act(() => deleteCell(c.id)) }}>
                      Delete
                    </button>
                  </span>
                  </span>
                </Td>
              </tr>
            ))}
          </ErpTable>
        </Panel>
      )}

      <NewCellDialog open={creating} electionId={electionId}
        onClose={() => setCreating(false)}
        onCreated={(id) => { cells.reload(); setOpenCell(id) }} />

      <CellDrawer key={openCell ?? 'none'} cellId={openCell}
        onClose={() => setOpenCell(null)} onChanged={() => cells.reload()} />
    </div>
  )
}

/** Create a cell at a station. Members are added afterwards, one at a time. */
function NewCellDialog({
  open, electionId, onClose, onCreated,
}: {
  open: boolean; electionId: string | null
  onClose: () => void; onCreated: (cellId: string) => void
}) {
  const stations = useLoad(
    // size is capped at 200 by UnitQueryService; 400 is a 400, not a bigger page.
    () => listUnits({ ...EMPTY_UNIT_FILTERS, type: ['POLLING_STATION'] }, 0, 200), [])
  const [stationId, setStationId] = useState('')
  const [cellLabel, setCellLabel] = useState('')
  const [contactName, setContactName] = useState('')
  // E.164; the country travels with it because the API takes the pair.
  const [contactPhone, setContactPhone] = useState('')
  const [contactCountry, setContactCountry] = useState<Country>(DEFAULT_COUNTRY)
  const [coords, setCoords] = useState('')
  const [targetSize, setTargetSize] = useState(10)
  const [meetingPoint, setMeetingPoint] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    if (!electionId || !stationId) return
    setSaving(true)
    setError(null)
    try {
      const cell = await createCell({
        electionId, stationId,
        label: cellLabel.trim() || null,
        targetSize,
        meetingPoint: meetingPoint.trim() || null,
        contactName: contactName.trim() || null,
        contactPhoneCountry: contactPhone ? countryOf(contactPhone, contactCountry) : null,
        contactPhone: contactPhone || null,
        geo: coords
          ? { latitude: Number(coords.split(',')[0]), longitude: Number(coords.split(',')[1]) }
          : null,
      })
      setCellLabel(''); setMeetingPoint(''); setContactName(''); setContactPhone(''); setContactCountry(DEFAULT_COUNTRY); setCoords('')
      onCreated(cell.id)
      onClose()
    } catch (e) {
      setError(explain(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="New cell"
      description="A cell is about ten neighbours at one station, with one of them accountable for the rest."
      footer={(
        <>
          <button type="button" className="erp-btn-ghost h-9" onClick={onClose}>Cancel</button>
          <button type="button" className="erp-btn erp-btn-primary h-9"
            disabled={saving || !stationId} onClick={() => { void submit() }}>
            {saving ? 'Creating…' : 'Create cell'}
          </button>
        </>
      )}>
      <div className="space-y-3">
        {/* The station fetch's own error was never rendered, so a 403 or a network
            failure showed an empty dropdown and no explanation. */}
        <ErrorStrip message={error ?? stations.error} />
        <Field label="Polling station" required
          hint={stations.loading ? 'Loading stations…'
            : (stations.data?.content ?? []).length === 0
              ? 'No polling stations in the register yet — import the geography hierarchy first.'
              : undefined}>
          <select className="erp-input h-9 w-full" value={stationId}
            disabled={stations.loading}
            onChange={(e) => setStationId(e.target.value)}>
            <option value="">Choose…</option>
            {(stations.data?.content ?? []).map((u) => (
              <option key={u.id} value={u.id}>{u.name} ({u.code})</option>
            ))}
          </select>
        </Field>
        <Field label="Cell name" hint="What the members call it — 'Kiamumbi Block C'.">
          <input className="erp-input h-9 w-full" value={cellLabel}
            onChange={(e) => setCellLabel(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Contact name" hint="Usually the coordinator.">
            <input className="erp-input h-9 w-full" value={contactName}
              onChange={(e) => setContactName(e.target.value)} />
          </Field>
          <Field label="Contact phone" hint="Stored encrypted; shown masked afterwards.">
            <PhoneInput
              value={contactPhone}
              onChange={setContactPhone}
              country={contactCountry}
              onCountryChange={setContactCountry}
            />
          </Field>
        </div>
        <Field label="Location"
          hint="Where the cell actually is. Use the device's GPS while standing there.">
          <div className="flex items-center gap-2">
            <input className="erp-input h-9 w-full" value={coords} readOnly
              placeholder="not captured" />
            <button type="button" className="erp-btn h-9 whitespace-nowrap"
              onClick={() => {
                navigator.geolocation?.getCurrentPosition(
                  (pos) => setCoords(`${pos.coords.latitude.toFixed(6)}, ${pos.coords.longitude.toFixed(6)}`),
                  // A refused or unavailable fix is not an error worth blocking on — the
                  // cell is still creatable without a point.
                  () => setCoords(''))
              }}>
              Use my location
            </button>
          </div>
        </Field>
        <Field label="Target size"
          hint="Ten, not twenty: a coordinator can chase 8–12 people between 06:00 and 17:00. Above that the tail goes uncontacted.">
          <input type="number" min={3} max={40} className="erp-input h-9 w-full"
            value={targetSize} onChange={(e) => setTargetSize(Number(e.target.value))} />
        </Field>
        <Field label="Meeting point" hint="Where they gather on the day, if they have one.">
          <input className="erp-input h-9 w-full" value={meetingPoint}
            onChange={(e) => setMeetingPoint(e.target.value)} />
        </Field>
      </div>
    </Modal>
  )
}
